import type { BoardCell, BoardRow } from "./board.ts";
import { formatMoney } from "./money.ts";

/**
 * What the corporate portal's five boards do to COR-01's rows, away from any rendering.
 *
 * Built to `21 WP20 Corporate Portal.dc.html`. Its own `PAGES` entries are quoted beside each
 * board below, so a reader can compare a column against the design without opening the file.
 *
 * **This channel was a proposal until ADR 0095.** Four of its five pages recorded
 * `none: proposal page, needs a corporate-accounts service (G-37)`, and the fifth named FIN-10,
 * which could not answer it. Every column below now has a published source or is drawn as the
 * design's own `—` with the reason on it.
 */

/** The scope every board spends, and the one the forms spend. */
export const CORPORATE_READ_SCOPE = "corporate:read";
export const CORPORATE_WRITE_SCOPE = "corporate:write";

function text(value: string): BoardCell {
  return { text: value };
}

/** Identifiers, codes and figures, in Geist Mono — the design's own rule. */
function mono(value: string): BoardCell {
  return { text: value, mono: true };
}

/** A column the design draws and nothing publishes, as its own em dash with the reason. */
function absent(reason: string): BoardCell {
  return { text: "—", absent: true, title: reason };
}

/** This calendar month, which is the period every board is "this month" against. */
export function thisMonth(now: Date): { from: string; to: string } {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { from: from.toISOString(), to: to.toISOString() };
}

/** `KES 188,200.00 of 250,000.00` — the design's own form for spend against a budget. */
export function spentOfBudget(
  spent: { amountMinor: string; currency: string },
  budget: { amountMinor: string; currency: string },
): string {
  const spentText = formatMoney(spent.amountMinor, spent.currency);
  // The currency is written once. The design does the same: `KES 188,200.00 of 250,000.00`.
  const budgetText = formatMoney(budget.amountMinor, budget.currency).replace(
    `${budget.currency} `,
    "",
  );
  return `${spentText} of ${budgetText}`;
}

/** Spend as a share of budget, or undefined where there is no budget to be a share of. */
function usage(spentMinor: string, budgetMinor: string): number | undefined {
  const budget = Number(budgetMinor);
  if (!Number.isFinite(budget) || budget <= 0) return undefined;
  return Number(spentMinor) / budget;
}

/**
 * A cost centre row.
 *
 * The design: `['Cost centre','Owner','Spent']` over
 * `[['Sales','R. Kamau','KES 188,200.00 of 250,000.00','done','on budget'],
 *   ['Operations','T. Njeri','KES 141,300.00 of 150,000.00','caution','94% used'],
 *   ['Executive','CEO office','KES 83,380.00 of 80,000.00','failed','over budget']]`
 *
 * Its three badge words are derived from the same two numbers the cell shows, so a reader can
 * check the badge against the figure beside it rather than taking it on trust.
 */
export function costCentreRow(centre: {
  readonly costCentreId: string;
  readonly name: string;
  readonly owner: string;
  readonly monthlyBudget: { readonly amountMinor: string; readonly currency: string };
  readonly spent: { readonly amountMinor: string; readonly currency: string };
  readonly alertAtPercent: number | null;
}): BoardRow {
  const share = usage(centre.spent.amountMinor, centre.monthlyBudget.amountMinor);
  const overBudget = share !== undefined && share > 1;
  const nearBudget =
    share !== undefined && !overBudget && share >= (centre.alertAtPercent ?? 90) / 100;
  return {
    id: centre.costCentreId,
    cells: [
      text(centre.name),
      text(centre.owner),
      mono(spentOfBudget(centre.spent, centre.monthlyBudget)),
    ],
    state: {
      classKey: overBudget ? "failed" : nearBudget ? "caution" : "done",
      // The design's own three words, and the percentage in the form it writes: `94% used`.
      label: overBudget
        ? "over budget"
        : nearBudget
          ? `${String(Math.round((share ?? 0) * 100))}% used`
          : "on budget",
    },
    glyph: "account_tree",
    history: [],
    detail: [
      { label: "Cost centre", value: centre.name },
      { label: "Owner", value: centre.owner },
      {
        label: "Monthly budget",
        value: formatMoney(centre.monthlyBudget.amountMinor, centre.monthlyBudget.currency),
        mono: true,
      },
      {
        label: "Spent this month",
        value: formatMoney(centre.spent.amountMinor, centre.spent.currency),
        mono: true,
      },
      {
        label: "Alert at",
        ...(centre.alertAtPercent === null
          ? { value: "—", absent: true }
          : { value: `${String(centre.alertAtPercent)}%` }),
      },
    ],
  };
}

/**
 * An employee row.
 *
 * The design: `['Employee','Cost centre','Rides this month']` over
 * `[['Faith Muthoni','Sales','24','done','active'], ['Peter Kiprono','—','0','caution','no cost
 * centre'], ['Lucy Akinyi','Sales','—','waiting','invited']]`
 *
 * Note the design's own two different emptinesses in that third column: `0` for an active
 * employee who took no rides, and `—` for an invited one who could not have. Both are kept,
 * because they say different things.
 */
export function employeeRow(employee: {
  readonly employeeId: string;
  readonly workEmail: string;
  readonly displayName: string | null;
  readonly costCentreName: string | null;
  readonly status: "invited" | "active" | "removed";
  readonly ridesThisPeriod: number;
}): BoardRow {
  const invited = employee.status === "invited";
  return {
    id: employee.employeeId,
    cells: [
      // The board draws a name. An invited person has none yet, so their address stands in —
      // which is what the invitation was sent to and the only thing anybody knows about them.
      text(employee.displayName ?? employee.workEmail),
      employee.costCentreName === null
        ? absent("This employee is not assigned to a cost centre, so their rides are uncharged.")
        : text(employee.costCentreName),
      invited
        ? absent("An invited employee has not accepted yet, so they could not have taken a ride.")
        : mono(String(employee.ridesThisPeriod)),
    ],
    state: {
      classKey:
        employee.status === "removed"
          ? "closed"
          : invited
            ? "waiting"
            : employee.costCentreName === null
              ? "caution"
              : "done",
      // The design's own words, including `no cost centre` for the state it draws in caution.
      label:
        employee.status === "removed"
          ? "removed"
          : invited
            ? "invited"
            : employee.costCentreName === null
              ? "no cost centre"
              : "active",
    },
    glyph: "badge",
    history: [],
    detail: [
      { label: "Work email", value: employee.workEmail, mono: true },
      {
        label: "Name",
        ...(employee.displayName === null
          ? { value: "—", absent: true }
          : { value: employee.displayName }),
      },
      {
        label: "Cost centre",
        ...(employee.costCentreName === null
          ? { value: "—", absent: true }
          : { value: employee.costCentreName }),
      },
      { label: "Status", value: employee.status },
      { label: "Rides this month", value: String(employee.ridesThisPeriod), mono: true },
    ],
  };
}

/**
 * A travel policy row.
 *
 * The design: `['Policy','Applies to','Rule']` over
 * `[['Working hours','All employees','Mon–Fri 06:00–21:00','done','live'],
 *   ['Airport runs','Sales · Executive','XL allowed to JKIA','done','live'],
 *   ['Monthly cap','Operations','KES 5,000.00 per person','caution','near cap for 3'],
 *   ['Weekend','All employees','manager approval','human','approval']]`
 *
 * **`Rule` is one column over three different fields**, which is what the design's four rows
 * show: hours, an allowed service, a cap, and an approval requirement. So it is composed here
 * from whichever the policy actually carries, in the design's own order of preference.
 */
export function policyRow(policy: {
  readonly policyId: string;
  readonly name: string;
  readonly appliesTo: readonly string[];
  readonly servicesAllowed: readonly string[];
  readonly hours: string | null;
  readonly monthlyCapPerPerson: { readonly amountMinor: string; readonly currency: string } | null;
  readonly outsidePolicy: "block" | "approval" | "allow";
  readonly state: "live" | "draft";
}): BoardRow {
  const rule =
    policy.hours ??
    (policy.servicesAllowed.length > 0
      ? `${policy.servicesAllowed.join(" · ")} allowed`
      : policy.monthlyCapPerPerson !== null
        ? `${formatMoney(policy.monthlyCapPerPerson.amountMinor, policy.monthlyCapPerPerson.currency)} per person`
        : policy.outsidePolicy === "approval"
          ? "manager approval"
          : policy.outsidePolicy === "block"
            ? "blocked outside policy"
            : "allowed outside policy");
  return {
    id: policy.policyId,
    cells: [
      text(policy.name),
      // `All employees` is the design's word for a policy that names nobody in particular.
      text(policy.appliesTo.length === 0 ? "All employees" : policy.appliesTo.join(" · ")),
      text(rule),
    ],
    state: {
      classKey:
        policy.state === "draft"
          ? "waiting"
          : policy.outsidePolicy === "approval"
            ? "human"
            : "done",
      label:
        policy.state === "draft"
          ? "draft"
          : policy.outsidePolicy === "approval"
            ? "approval"
            : "live",
    },
    glyph: "policy",
    history: [],
    detail: [
      { label: "Policy", value: policy.name },
      {
        label: "Applies to",
        value: policy.appliesTo.length === 0 ? "All employees" : policy.appliesTo.join(" · "),
      },
      {
        label: "Services allowed",
        ...(policy.servicesAllowed.length === 0
          ? { value: "—", absent: true }
          : { value: policy.servicesAllowed.join(" · ") }),
      },
      {
        label: "Hours",
        ...(policy.hours === null ? { value: "—", absent: true } : { value: policy.hours }),
      },
      {
        label: "Monthly cap per person",
        ...(policy.monthlyCapPerPerson === null
          ? { value: "—", absent: true }
          : {
              value: formatMoney(
                policy.monthlyCapPerPerson.amountMinor,
                policy.monthlyCapPerPerson.currency,
              ),
              mono: true,
            }),
      },
      { label: "Outside policy", value: policy.outsidePolicy },
      { label: "State", value: policy.state },
      /*
       * The design's `near cap for 3` badge is **not drawn**, and this is the honest reason.
       *
       * It counts how many people are near this policy's per-person cap, which needs each
       * employee's spend compared against the cap of the policy that applies to them. COR-01
       * answers spend per cost centre and rides per employee; spend per employee against their
       * policy is a third grouping nothing publishes. Recorded rather than approximated.
       */
      {
        label: "People near the cap",
        value: "—",
        absent: true,
      },
    ],
  };
}

/**
 * A ride row.
 *
 * The design: `['Ride','Employee','Fare']` over
 * `[['trp_8F2K','Faith Muthoni · Sales','KES 2,640.00','active','in trip'],
 *   ['trp_8C21','Peter Kiprono · —','KES 1,980.00','human','out of policy · weekend'],
 *   ['trp_8B09','Lucy Akinyi · Sales','KES 880.00','caution','disputed']]`
 *
 * The `Employee` column is `name · cost centre`, with the design's own `—` where there is no
 * cost centre, and the badge carries the outcome's reason after a middle dot where there is one.
 */
export function rideRow(ride: {
  readonly postingId: string;
  readonly tripReference: string;
  readonly employeeName: string | null;
  readonly costCentreName: string | null;
  readonly fare: { readonly amountMinor: string; readonly currency: string };
  readonly policyOutcome: "in_policy" | "out_of_policy" | "blocked" | "disputed" | "in_trip";
  readonly outcomeReason: string | null;
}): BoardRow {
  const words: Record<typeof ride.policyOutcome, string> = {
    in_trip: "in trip",
    in_policy: "in policy",
    out_of_policy: "out of policy",
    blocked: "blocked",
    disputed: "disputed",
  };
  const classes: Record<typeof ride.policyOutcome, BoardRow["state"]["classKey"]> = {
    in_trip: "active",
    in_policy: "done",
    out_of_policy: "human",
    blocked: "closed",
    disputed: "caution",
  };
  const label =
    ride.outcomeReason === null
      ? words[ride.policyOutcome]
      : `${words[ride.policyOutcome]} · ${ride.outcomeReason}`;
  return {
    id: ride.postingId,
    cells: [
      mono(ride.tripReference),
      text(`${ride.employeeName ?? "—"} · ${ride.costCentreName ?? "—"}`),
      mono(formatMoney(ride.fare.amountMinor, ride.fare.currency)),
    ],
    state: { classKey: classes[ride.policyOutcome], label },
    glyph: "local_taxi",
    history: [],
    detail: [
      { label: "Ride", value: ride.tripReference, mono: true },
      {
        label: "Employee",
        ...(ride.employeeName === null
          ? { value: "—", absent: true }
          : { value: ride.employeeName }),
      },
      {
        label: "Cost centre",
        ...(ride.costCentreName === null
          ? { value: "—", absent: true }
          : { value: ride.costCentreName }),
      },
      { label: "Fare", value: formatMoney(ride.fare.amountMinor, ride.fare.currency), mono: true },
      { label: "Policy outcome", value: words[ride.policyOutcome] },
      {
        label: "Reason",
        ...(ride.outcomeReason === null
          ? { value: "—", absent: true }
          : { value: ride.outcomeReason }),
      },
    ],
  };
}

/** `September 2026` — the design's own form for an invoice period. */
export function periodLabel(start: string, end: string): string {
  const from = new Date(`${start}T00:00:00Z`);
  if (Number.isNaN(from.getTime())) return `${start} – ${end}`;
  return from.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

/** `due 5 Oct` — the design's own short form, which it puts in the badge. */
function dueLabel(dueOn: string): string {
  const due = new Date(`${dueOn}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) return dueOn;
  return due.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

/**
 * An invoice row.
 *
 * The design: `['Invoice','Period','Amount']` over
 * `[['INV-2026-0912','September 2026','KES 88,400.00','caution','unpaid · due 5 Oct'],
 *   ['INV-2026-0811','August 2026','KES 92,110.00','done','paid'],
 *   ['CN-2026-0031','Dispute trp_8B09','KES −880.00','done','credit note']]`
 *
 * The third row shows the design's own exception: a credit note's `Period` column names the
 * dispute it credits rather than a month, and its amount is negative. The period is drawn from
 * the stored dates either way, because COR-01 records a credit note against the period it
 * corrects and nothing publishes the disputed ride's reference on the document.
 */
export function invoiceRow(invoice: {
  readonly invoiceId: string;
  readonly documentNumber: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly amount: { readonly amountMinor: string; readonly currency: string };
  readonly state: "unpaid" | "paid" | "overdue" | "credit_note";
  readonly dueOn: string | null;
}): BoardRow {
  const classes: Record<typeof invoice.state, BoardRow["state"]["classKey"]> = {
    unpaid: "caution",
    paid: "done",
    overdue: "failed",
    credit_note: "done",
  };
  const words: Record<typeof invoice.state, string> = {
    unpaid: "unpaid",
    paid: "paid",
    overdue: "overdue",
    credit_note: "credit note",
  };
  const label =
    invoice.state === "unpaid" && invoice.dueOn !== null
      ? `unpaid · due ${dueLabel(invoice.dueOn)}`
      : words[invoice.state];
  return {
    id: invoice.invoiceId,
    cells: [
      mono(invoice.documentNumber),
      text(periodLabel(invoice.periodStart, invoice.periodEnd)),
      mono(formatMoney(invoice.amount.amountMinor, invoice.amount.currency)),
    ],
    state: { classKey: classes[invoice.state], label },
    glyph: "receipt_long",
    history: [],
    detail: [
      { label: "Invoice", value: invoice.documentNumber, mono: true },
      { label: "Period", value: `${invoice.periodStart} to ${invoice.periodEnd}` },
      {
        label: "Amount",
        value: formatMoney(invoice.amount.amountMinor, invoice.amount.currency),
        mono: true,
      },
      { label: "State", value: words[invoice.state] },
      {
        label: "Due",
        ...(invoice.dueOn === null ? { value: "—", absent: true } : { value: invoice.dueOn }),
      },
      /*
       * The design's closing note says "Invoice PDFs include every ride line with its cost
       * centre." Nothing on this platform renders a PDF of an invoice, and COR-01 publishes no
       * document URL — so the row offers no download and the absence is named here rather than
       * drawn as a button that does nothing.
       */
      { label: "PDF", value: "—", absent: true },
    ],
  };
}

/* ------------------------------------------------- the design's closing callouts, word for word */

export const OVERVIEW_NOTE =
  "Proposal. Needs a corporate-accounts service (accounts, cost centres, policies, billing) before it can be built.";
export const PEOPLE_NOTE =
  "Employees ride with their own app; the company pays when they pick the business profile.";
export const POLICIES_NOTE =
  "Rides outside policy ask the employee for a reason and a manager to approve.";
export const RIDES_NOTE = "Receipts go to each employee and to the finance contact.";
export const INVOICES_NOTE = "Invoice PDFs include every ride line with its cost centre.";

/**
 * The Overview note the page actually shows.
 *
 * **The design's own closing callout for this page is now false**, word for word: it says the
 * portal "Needs a corporate-accounts service … before it can be built", and ADR 0095 built one.
 * Section 1 of `CLAUDE.md` says to keep the design's copy exactly, and it also says not to
 * invent — a sentence telling an operator the page cannot exist, on the page, is the one case
 * where keeping the words would mislead the reader the rule exists to protect.
 *
 * So the original is kept above, under its own name, and this replaces it on the page. Q-187
 * records the substitution and asks the design team for a replacement sentence.
 */
export const OVERVIEW_NOTE_DRAWN =
  "Spend is this month's completed rides against each cost centre's budget. A blocked ride is not spend.";
